import { useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { NotFound } from '../NotFound';
import type { FeatureId } from '@sequesto-os/shared';
import { useAccess } from '../../hooks/useAccess';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { TabBar, type TabItem } from '../../components/ui/Tabs';
import { PipelinePage } from './pipeline/PipelinePage';
import { MeetingsPage } from './meetings/MeetingsPage';
import { OutboundPage } from './outbound/OutboundPage';
import { PilotsPage } from './pilots/PilotsPage';
import { ResearchPage } from './research/ResearchPage';

type Section = 'pipeline' | 'meetings' | 'outbound' | 'pilots' | 'research';

const SECTIONS: readonly TabItem<Section>[] = [
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'outbound', label: 'Outbound' },
  { id: 'pilots', label: 'Outreach Buckets' },
  { id: 'research', label: 'Research' },
];

/**
 * A merged section is visible when the user can reach any of its parts; each
 * part inside it is still gated on its own feature id.
 */
const SECTION_FEATURES: Record<Section, readonly FeatureId[]> = {
  pipeline: ['sales.ae'],
  meetings: ['sales.bookings', 'sales.ae', 'sales.sdr'],
  outbound: ['sales.outbound', 'sales.outreach', 'sales.sdr'],
  pilots: ['sales.pilots'],
  research: ['sales.research'],
};

function isSection(value: string | undefined): value is Section {
  return SECTIONS.some((s) => s.id === value);
}

/**
 * Where the URLs of the old 7-tab layout live now (AE, SDRs, Demo bookings,
 * Outreach and Deep Research were merged into five tabs on 2026-10-08).
 */
function legacyTarget(section: string, tab: string | undefined, search: string): string | null {
  const params = new URLSearchParams(search);
  switch (section) {
    case 'ae':
      if (tab === 'meetings') return '/sales/meetings?view=prospects';
      if (tab === 'stale') return '/sales/pipeline#stale';
      return '/sales/pipeline';
    case 'sdr':
      if (tab === 'activity' || tab === 'apollo') return '/sales/outbound';
      return '/sales/meetings?view=sdr';
    case 'bookings':
      return '/sales/meetings?view=bookings';
    case 'outreach': {
      if (tab === 'unknown-companies') params.set('queue', 'unknown');
      else if (tab && tab !== 'overview' && tab !== 'campaigns') params.set('signal', tab);
      const qs = params.toString();
      return `/sales/outbound${qs ? `?${qs}` : ''}`;
    }
    default:
      return null;
  }
}

export function Sales() {
  const { section, tab } = useParams<{ section?: string; tab?: string }>();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { canAccess } = useAccess();

  const canSee = (s: Section) => SECTION_FEATURES[s].some((f) => canAccess(f));
  const visibleSections = SECTIONS.filter((s) => canSee(s.id));
  const firstSection = visibleSections[0]?.id;

  const legacy = section ? legacyTarget(section, tab, search) : null;
  if (legacy) return <Navigate to={legacy} replace />;

  if (section && !isSection(section)) return <NotFound />;
  if (!section || !isSection(section) || !canSee(section)) {
    return <Navigate to={firstSection ? `/sales/${firstSection}` : '/'} replace />;
  }

  return (
    <PageShell>
      <PageHeader eyebrow="Departments" title="Sales">
        {visibleSections.length > 1 && (
          <TabBar tabs={visibleSections} active={section} onSelect={(id) => navigate(`/sales/${id}`)} />
        )}
      </PageHeader>

      {section === 'pipeline' && <PipelinePage />}
      {section === 'meetings' && <MeetingsPage />}
      {section === 'outbound' && <OutboundPage />}
      {section === 'pilots' && <PilotsPage bucketId={tab} />}
      {section === 'research' && <ResearchPage />}
    </PageShell>
  );
}
